import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../order.schema';
import { buildEspecificacao } from '../../utils/buildEspecificacao';

interface SelectOption {
  id: number;
  name: string;
}

interface UseEspecificacaoAutoFillArgs {
  itemPath: ItemPath;
  formats: SelectOption[];
  finishings: SelectOption[];
}

const toNum = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Auto-preenche o campo Especificação (nomeCustomizado) a partir dos campos do item,
 * enquanto o valor atual for vazio ou o último valor auto-gerado. Edição manual do
 * vendedor interrompe a escrita; limpar o campo reativa o auto-fill.
 */
export const useEspecificacaoAutoFill = ({ itemPath, formats, finishings }: UseEspecificacaoAutoFillArgs) => {
  const { control, getValues, setValue } = useFormContext<OrderSchemaType>();
  const lastAutoRef = useRef<string | null>(null);

  const itemType = useWatch({ control, name: `${itemPath}.itemType` as any }) as string | undefined;
  const productNorm = useWatch({ control, name: `${itemPath}.productNorm` as any }) as string | undefined;
  const stockStandards = useWatch({ control, name: `${itemPath}.stockStandards` as any }) as string | undefined;
  const productName = useWatch({ control, name: `${itemPath}.finalProduct.produto` as any }) as string | undefined;
  const materialName = useWatch({ control, name: `${itemPath}.feedstock.materiaPrima` as any }) as string | undefined;
  const resourceName = useWatch({ control, name: `${itemPath}.resource.insumo` as any }) as string | undefined;
  const formatId = useWatch({ control, name: `${itemPath}.formatId` as any }) as number | undefined;
  const finishingId = useWatch({ control, name: `${itemPath}.finishingId` as any }) as number | undefined;
  const diameter = useWatch({ control, name: `${itemPath}.diameter` as any });
  const thickness = useWatch({ control, name: `${itemPath}.thickness` as any });
  const width = useWatch({ control, name: `${itemPath}.width` as any });
  const length = useWatch({ control, name: `${itemPath}.length` as any });
  const diameterToleranceMinus = useWatch({ control, name: `${itemPath}.diameterToleranceMinus` as any });
  const diameterTolerancePlus = useWatch({ control, name: `${itemPath}.diameterTolerancePlus` as any });
  const thicknessToleranceMinus = useWatch({ control, name: `${itemPath}.thicknessToleranceMinus` as any });
  const thicknessTolerancePlus = useWatch({ control, name: `${itemPath}.thicknessTolerancePlus` as any });
  const widthToleranceMinus = useWatch({ control, name: `${itemPath}.widthToleranceMinus` as any });
  const widthTolerancePlus = useWatch({ control, name: `${itemPath}.widthTolerancePlus` as any });
  const lengthToleranceMinus = useWatch({ control, name: `${itemPath}.lengthToleranceMinus` as any });
  const lengthTolerancePlus = useWatch({ control, name: `${itemPath}.lengthTolerancePlus` as any });
  const quantityPieces = useWatch({ control, name: `${itemPath}.quantityPieces` as any });
  const additionalData = useWatch({ control, name: `${itemPath}.additionalData` as any }) as string | undefined;

  useEffect(() => {
    const type = String(itemType || '');
    if (type === 'SERVICE') return;

    const nome = type === 'PRODUCT' ? productName : type === 'MATERIAL' ? materialName : resourceName;
    const norma = type === 'PRODUCT' ? productNorm : type === 'MATERIAL' ? stockStandards : undefined;
    const formato = formats.find(f => Number(f.id) === Number(formatId))?.name;
    const acabamento = finishings.find(f => Number(f.id) === Number(finishingId))?.name;

    const base = buildEspecificacao({
      norma,
      nome,
      formato,
      acabamento,
      diameter: toNum(diameter),
      thickness: toNum(thickness),
      width: toNum(width),
      length: toNum(length),
      quantityPieces: toNum(quantityPieces),
      diameterToleranceMinus: toNum(diameterToleranceMinus),
      diameterTolerancePlus: toNum(diameterTolerancePlus),
      thicknessToleranceMinus: toNum(thicknessToleranceMinus),
      thicknessTolerancePlus: toNum(thicknessTolerancePlus),
      widthToleranceMinus: toNum(widthToleranceMinus),
      widthTolerancePlus: toNum(widthTolerancePlus),
      lengthToleranceMinus: toNum(lengthToleranceMinus),
      lengthTolerancePlus: toNum(lengthTolerancePlus),
    });

    const additional = typeof additionalData === 'string' ? additionalData.trim() : '';
    const newAuto = additional ? (base ? `${base} - ${additional}` : additional) : base;

    const current = String(getValues(`${itemPath}.nomeCustomizado` as any) ?? '').trim();

    // Texto manual do vendedor vence: só escreve sobre vazio ou sobre o último auto-gerado.
    if (current !== '' && current !== lastAutoRef.current && current !== newAuto) return;

    if (current !== newAuto) {
      setValue(`${itemPath}.nomeCustomizado` as any, newAuto, { shouldDirty: true, shouldValidate: false });
    }
    lastAutoRef.current = newAuto;
  }, [
    itemType, productNorm, stockStandards, productName, materialName, resourceName,
    formatId, finishingId, diameter, thickness, width, length, quantityPieces, additionalData,
    diameterToleranceMinus, diameterTolerancePlus, thicknessToleranceMinus, thicknessTolerancePlus,
    widthToleranceMinus, widthTolerancePlus,
    lengthToleranceMinus, lengthTolerancePlus,
    formats, finishings, itemPath, getValues, setValue,
  ]);
};
